import React, { useState } from "react";
import { Container, Alert, ConnectedAlert, ProgressBar } from "../../../../components";
import { Icon, Text, Heading, Button, Link, List } from "@telia/teddy";
import _compact from "lodash/compact";
import _get from "lodash/get";
import { connect, useDispatch } from "react-redux";
import { MaybeEmpty } from "../../../../../../constants/utils";
import {
    AgreementToKeep,
    ExistingAgreement,
    ExistingService,
    ExistingSubscription,
    RootState,
} from "../../../../../../models";
import { getErrorMessage, isOrderNotFound as isOrderNotFoundSelector } from "../../../../app/state/selectors";
import { getBankIdErrorMessage } from "../../../../authentication/state/selectors";
import BankIdSection from "../../../../checkout/components/molecules/BankIdSectionTeliaBroker/BankIdSectionTeliaBroker";
import { submitTransferOwnershipOrder } from "../../../../checkout/state/actions/transferOwnershipActions";
import {
    isExistingLegalOwnerVerified as isExistingLegalOwnerVerifiedSelector,
    getHasSubmittedTransferOrder,
    getTransferExistingSwitchAgreement,
    getTransferExistingLeaseAgreements,
    getTransferAgreementsToKeep,
    getTransferExistingServicesOnBAN,
    getTransferExistingInsurance,
    getTransferExistingSubscription,
} from "../../../../checkout/state/selectors/accountSelectors";
import { getLegalOwnerFullName } from "../../../../checkout/state/selectors/customerInfoSelectors";
import { selectors as LOADING_SELECTORS } from "../../../../loading";
import Agreement from "../../../components/molecules/Agreement/Agreement";
import RelatedServices from "../../../components/molecules/RelatedServices/RelatedServices";
import SubscriptionInformation from "../../../components/molecules/SubscriptionInformation/SubscriptionInformation";
import TEXT from "../../../text-consts";
import {
    SCREEN_INSURANCE,
    convertAgreementToBoxData,
    getKeepAgreementDescription,
    isSwitchPlusInsurance,
} from "../../../utils";
import "./LandingPage.less";

interface StateProps {
    agreementsToKeep: AgreementToKeep;
    existingInsurance?: ExistingService;
    existingLeaseAgreements?: ExistingAgreement[];
    existingServicesOnBAN: ExistingService[];
    existingSubscription: MaybeEmpty<ExistingSubscription>;
    existingSwitchAgreement?: ExistingAgreement;
    hasSubmittedTransferOrder: boolean;
    isExistingLegalOwnerVerified: boolean;
    isFetchingData: boolean;
    isOrderNotFound: boolean;
    legalOwnerFullName: string;
    logicalErrorMessage?: string;
}

type LandingPageB2CtoB2CProps = StateProps;

const LandingPageB2CtoB2C: React.FC<LandingPageB2CtoB2CProps> = ({
    // Redux (data)
    isFetchingData,
    isOrderNotFound,
    legalOwnerFullName,
    logicalErrorMessage,
    isExistingLegalOwnerVerified,
    hasSubmittedTransferOrder,
    existingSwitchAgreement,
    existingLeaseAgreements,
    agreementsToKeep,
    existingInsurance,
    existingServicesOnBAN,
    existingSubscription,
}) => {
    const dispatch = useDispatch();
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);

    function handleSubmitTransferOwnershipOrder() {
        setHasClickedSubmit(true);
        dispatch(submitTransferOwnershipOrder());
    }

    if (hasSubmittedTransferOrder) {
        return (
            <div className="tlo-landing-page">
                <div className="tlo-landing-page__heading-container">
                    <Heading mb="100" as="h2" variant="title-500">
                        {TEXT.LANDING_PAGE.HEADING}
                    </Heading>
                </div>
                <Container size="medium">
                    <div className="tlo-landing-page__receipt">
                        <Icon name="check-circle-filled" className="tlo-landing-page__check-icon" />
                        <Heading mb="100" as="h2" variant="title-200" data-di-mask>
                            {TEXT.LANDING_PAGE.ORDER_CONFIRMATION_HEADER}
                        </Heading>
                        <Text mb="100" data-di-mask>
                            {TEXT.LANDING_PAGE.ORDER_CONFIRMATION_DESCRIPTION(legalOwnerFullName)}
                        </Text>
                        <div className="tlo-landing-page__receipt-back-link">
                            <Link variant="standalone" href="/">
                                <Icon name="arrow-left" />
                                {TEXT.LANDING_PAGE.BACK}
                            </Link>
                        </div>
                    </div>
                </Container>
            </div>
        );
    }

    if (isOrderNotFound) {
        return (
            <div className="tlo-landing-page">
                <div className="tlo-landing-page__heading-container">
                    <Heading mb="100" as="h1" variant="title-600">
                        {TEXT.LANDING_PAGE.HEADING}
                    </Heading>
                </div>
                <Container size="medium">
                    <Text mb="100">{TEXT.LANDING_PAGE.ORDER_NOT_FOUND}</Text>
                </Container>
            </div>
        );
    }

    const existingAgreements = _compact([existingSwitchAgreement, ...(existingLeaseAgreements || [])]);
    const existingAgreementsToKeep = existingAgreements.filter((agreement) => _get(agreementsToKeep, agreement.type));
    const existingAgreementsToTransfer = existingAgreements.filter(
        (agreement) => !_get(agreementsToKeep, agreement.type),
    );

    const hasAgreementsToKeep = existingAgreementsToKeep.length > 0;
    const hasAgreementsToTransfer = existingAgreementsToTransfer.length > 0;

    const isInsuranceSwitchPlus = isSwitchPlusInsurance(existingInsurance?.type);

    return (
        <div className="tlo-landing-page">
            <div className="tlo-landing-page__heading-container">
                <Heading mb="100" as="h1" variant="title-600">
                    {TEXT.LANDING_PAGE.HEADING}
                </Heading>
            </div>
            <ProgressBar isFetchingData={isFetchingData} standByColor="light-grey" />
            <ConnectedAlert />
            {logicalErrorMessage && (
                <Container size="medium">
                    <Alert kind="warning">{logicalErrorMessage}</Alert>
                </Container>
            )}
            <Container size="medium">
                <Heading mb="100" as="h2" variant="title-200" data-di-mask>
                    {TEXT.LANDING_PAGE.REQUEST_RECEIVED(legalOwnerFullName)}
                </Heading>

                <Text mb="100" variant="additional-100" data-di-mask>
                    {TEXT.LANDING_PAGE.LIST.DESCRIPTION(legalOwnerFullName)}
                </Text>
                <List layout="vertical" gapSize="small">
                    <List.Item>
                        <List.Illustration variant="image">
                            <Icon name="checkmark" aria-hidden="true" />
                        </List.Illustration>
                        <List.Content>{TEXT.LANDING_PAGE.LIST.ITEM_1}</List.Content>
                    </List.Item>
                    <List.Item>
                        <List.Illustration variant="image">
                            <Icon name="checkmark" aria-hidden="true" />
                        </List.Illustration>
                        <List.Content>{TEXT.LANDING_PAGE.LIST.ITEM_2}</List.Content>
                    </List.Item>
                </List>
            </Container>
            {!isExistingLegalOwnerVerified && (
                <Container size="medium" noPaddingTop>
                    <Text mb="100" variant="additional-100">
                        {TEXT.LANDING_PAGE.BANK_ID_CONSENT}
                    </Text>
                    <div className="tlo-landing-page__bankid-container">
                        <BankIdSection
                            referer="transfer-consent-b2c"
                            skipDisplayDescription={true}
                            skipDisplayError={true}
                        />
                    </div>
                </Container>
            )}
            {isExistingLegalOwnerVerified && (
                <>
                    <Container size="medium" whiteBg>
                        <SubscriptionInformation heading={TEXT.LANDING_PAGE.INFORMATION_HEADING.MAIN} showNewOwner />
                        <RelatedServices services={existingServicesOnBAN} />
                        {hasAgreementsToTransfer && (
                            <Heading
                                mb="100"
                                as="h2"
                                variant="title-100"
                                className="tlo-landing-page__related-phone-title"
                            >
                                {TEXT.LANDING_PAGE.RELATED_PHONE}
                            </Heading>
                        )}

                        <div>
                            {existingAgreementsToTransfer.map((agreement, index) => (
                                <Agreement
                                    key={index}
                                    {...convertAgreementToBoxData(agreement)}
                                    existingInsurance={existingInsurance}
                                />
                            ))}
                            {hasAgreementsToKeep && (
                                <div className="tlo-landing-page__keep-agreements-container">
                                    <Heading
                                        mb="100"
                                        as="h2"
                                        variant="title-100"
                                        className="tlo-information__table__table-heading"
                                    >
                                        <div>
                                            Avtaler som <u>ikke</u> overføres
                                        </div>
                                    </Heading>
                                    <span>{TEXT.LANDING_PAGE.KEEP_AGREEMENT_DESCRIPTION}</span>
                                    {existingAgreementsToKeep.map((agreement, index) => (
                                        <Agreement
                                            key={index}
                                            {...convertAgreementToBoxData(agreement)}
                                            existingInsurance={
                                                isInsuranceSwitchPlus ? SCREEN_INSURANCE : existingInsurance
                                            }
                                            footer={
                                                <div className="tlo-landing-page__keep-agreement-footer">
                                                    <Icon name="info" />
                                                    <p>
                                                        {getKeepAgreementDescription(
                                                            agreement.type,
                                                            existingInsurance?.type,
                                                            existingSubscription.newLegalOwner,
                                                        )}
                                                    </p>
                                                </div>
                                            }
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </Container>
                    <Container className="tlo-landing-page__consent-container" size="medium">
                        <Button
                            variant="primary"
                            type="submit"
                            onClick={handleSubmitTransferOwnershipOrder}
                            disabled={hasClickedSubmit}
                        >
                            {TEXT.LANDING_PAGE.BUTTON.CONFIRM_TRANSFER}
                        </Button>
                    </Container>
                </>
            )}
        </div>
    );
};

const mapStateToProps: (state: RootState) => StateProps = (state) => ({
    isFetchingData: LOADING_SELECTORS.isLoading(state),
    isOrderNotFound: isOrderNotFoundSelector(state),
    legalOwnerFullName: getLegalOwnerFullName(state),
    isExistingLegalOwnerVerified: isExistingLegalOwnerVerifiedSelector(state),
    hasSubmittedTransferOrder: getHasSubmittedTransferOrder(state),
    existingSwitchAgreement: getTransferExistingSwitchAgreement(state),
    existingLeaseAgreements: getTransferExistingLeaseAgreements(state),
    agreementsToKeep: getTransferAgreementsToKeep(state),
    existingServicesOnBAN: getTransferExistingServicesOnBAN(state),
    existingInsurance: getTransferExistingInsurance(state),
    existingSubscription: getTransferExistingSubscription(state),
    logicalErrorMessage: getErrorMessage(state) || getBankIdErrorMessage(state),
});

export default connect(mapStateToProps)(LandingPageB2CtoB2C);
